<template>
    <section class="FilterMobileSection">
        <button class="FilterBtn" type="button" @click="openFiltersModal">
            <i class="lnr-equalizer Icon"></i>Filters
        </button>
        <span class="Badge" v-if="activeFilters > 0">{{ activeFilters }}</span>
    </section>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useMobile } from '@composables/useMobile';
import { useMobileModal } from '@composables/useMobileModal';
import { useSearchStore } from '@stores/useSearchStore';
import Filters from '@components/Filters/Filters.vue';
import type { ModalStateFooter } from '@app/interfaces/modalState.interface';
import { FilterType } from '@studyportals/omnisearch-interfaces';
import { PortalType } from '@studyportals/domain-client';
import { SearchType } from '@domain/enums/SearchType';

const { isMobile } = useMobile();
const { open: openMobileModal, close: closeMobileModal } = useMobileModal();
const searchStore = useSearchStore();

const hasFilters = computed(() => searchStore.changeableFiltersSelection.length > 0);

const activeFilters = computed<number>(() => {
    const { portalType, searchType } = searchStore;
	return searchStore.filtersSelection
        .filter(filter => filter.type !== FilterType.SORTING &&
            (filter.type !== FilterType.EDUCATION_LEVEL || portalType === PortalType.DISTANCE_LEARNING) &&
            (filter.type !== FilterType.DEGREE_TYPE || searchType !== SearchType.UNIVERSITY_SEARCH))
        .reduce((acc, filter) => acc + filter.values.length, 0);
});

const clearAllFilters = async (): Promise<void> => {
    await searchStore.cleanFilters();
};

const footer = computed<ModalStateFooter>(() => ({
    actionText: 'Show Results',
    action: (): void => closeMobileModal(),
    actionType: 'primary',
    ...(hasFilters.value && {
        clearText: 'Clear all',
        clear: (): void => void clearAllFilters()
    })
}));

const openFiltersModal = (): void => {
    if (!isMobile.value) return;

    openMobileModal(Filters, { footer: footer.value });
};
</script>

<style scoped lang="scss">
.FilterMobileSection {
    display: flex;
    align-items: center;
    position: relative;

    .FilterBtn {
        display: flex;
        gap: 0.5rem;
        align-items: center;
        border: 1px solid $Grey-900;
        border-radius: 0.25rem;
        cursor: pointer;
        background: $Grey-900;
        color: $White;
        height: 2rem;

        @include TextStyle(UiLargeRegular);
        @include PaddingAreas(0.5);

        .Icon {
            font-size: 1rem;
        }
    }
    .Badge {
        display: flex;
        gap: 0.63rem;
        position: absolute;
        flex-direction: column;
        color: $White;
        justify-content: center;
        align-items: center;
        left: 4.5rem;
        bottom: 1.25rem;
        height: 1rem;
        width: 1rem;
        border-radius: 50%;
        background-color: $Blue-800;

        @include PaddingAreas(0.25);
        @include TextStyle(UiSmallRegular);
    }

    @include Breakpoint(Large, ExtraLarge) {
        display: none;
    }
}

</style>
